
// ---------------------------------------------------------------------------
// Occasions — a label only (list filter, AI phone prompt, automation context). Pages no longer depend on
// it: every campaign page is the owner's own flyer + text in one universal layout.
// ---------------------------------------------------------------------------
export const CAMPAIGN_OCCASIONS = [
  'GRAND_OPENING',
  'SOFT_OPENING',
  'NOW_OPEN',
  'BACK_TO_SCHOOL',
  'HOLIDAY',
  'GIVEAWAY',
  'NEW_GUEST_OFFER',
  'CUSTOM',
] as const;
export type CampaignOccasion = (typeof CAMPAIGN_OCCASIONS)[number];

export interface CampaignOccasionMeta {
  label: string;
  /** One neutral line about the occasion — never an offer (the demo seed uses it as page text). */
  description: string;
}

export const CAMPAIGN_OCCASION_META: Record<CampaignOccasion, CampaignOccasionMeta> = {
  GRAND_OPENING: {
    label: 'Grand opening',
    description: 'Celebrate opening day with your first guests.',
  },
  SOFT_OPENING: {
    label: 'Soft opening',
    description: 'Preview days before the full launch.',
  },
  NOW_OPEN: {
    label: 'Now open',
    description: 'Bring in first visits now that the doors are open.',
  },
  BACK_TO_SCHOOL: {
    label: 'Back to school',
    description: 'Fresh nails for the new school year.',
  },
  HOLIDAY: {
    label: 'Holiday',
    description: "Seasonal promos — Mother's Day, Christmas, Valentine's.",
  },
  GIVEAWAY: {
    label: 'Giveaway',
    description: 'A prize drawing to grow your list.',
  },
  NEW_GUEST_OFFER: {
    label: 'New-guest offer',
    description: 'An always-on welcome for first-time clients.',
  },
  CUSTOM: {
    label: 'Custom',
    description: 'Any other promo — your flyer, your words.',
  },
};

// ---------------------------------------------------------------------------
// Kind — STANDARD is an owner-made campaign; NEW_CLIENT_OFFER_PAGE is the salon's one auto-made ad page for its
// New Client Offer (`/new-client`). Lists the owner manages or copies exclude it; reports include it.
// ---------------------------------------------------------------------------
export const CAMPAIGN_KINDS = ['STANDARD', 'NEW_CLIENT_OFFER_PAGE'] as const;
export type CampaignKind = (typeof CAMPAIGN_KINDS)[number];

export function isNewClientOfferPageKind(kind: CampaignKind): boolean {
  return kind === 'NEW_CLIENT_OFFER_PAGE';
}

// ---------------------------------------------------------------------------
// Status — stored vs displayed. SCHEDULED / ENDED are derived from dates.
// ---------------------------------------------------------------------------
export const CAMPAIGN_STATUSES = ['DRAFT', 'PUBLISHED', 'PAUSED', 'ARCHIVED'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CAMPAIGN_DISPLAY_STATUSES = [
  'DRAFT',
  'SCHEDULED',
  'PUBLISHED',
  'PAUSED',
  'ENDED',
  'ARCHIVED',
] as const;
export type CampaignDisplayStatus = (typeof CAMPAIGN_DISPLAY_STATUSES)[number];

export const CAMPAIGN_DISPLAY_STATUS_LABELS: Record<CampaignDisplayStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  PUBLISHED: 'Live',
  PAUSED: 'Paused',
  ENDED: 'Ended',
  ARCHIVED: 'Archived',
};

export interface CampaignDateWindow {
  status: CampaignStatus;
  /** ISO calendar date `YYYY-MM-DD` in the salon's timezone, or null. */
  startDate: string | null;
  endDate: string | null;
}

/** Pure date comparison on `YYYY-MM-DD` strings (lexicographic order == chronological). */
export function deriveCampaignDisplayStatus(
  campaign: CampaignDateWindow,
  todayIsoDate: string
): CampaignDisplayStatus {
  if (campaign.status !== 'PUBLISHED') return campaign.status;
  if (campaign.endDate && todayIsoDate > campaign.endDate) return 'ENDED';
  if (campaign.startDate && todayIsoDate < campaign.startDate) return 'SCHEDULED';
  return 'PUBLISHED';
}

/**
 * The public page is up: live, scheduled (shows its "opens soon" state) or paused. A draft, an ended or an
 * archived campaign has no page worth sharing.
 */
export function isCampaignPageUp(displayStatus: CampaignDisplayStatus): boolean {
  return displayStatus === 'PUBLISHED' || displayStatus === 'SCHEDULED' || displayStatus === 'PAUSED';
}

/** The public form accepts entries only while the campaign is live and inside its window. */
export function isCampaignFormOpen(campaign: CampaignDateWindow, todayIsoDate: string): boolean {
  return deriveCampaignDisplayStatus(campaign, todayIsoDate) === 'PUBLISHED';
}

// ---------------------------------------------------------------------------
// Traffic source — tags links so analytics can attribute each lead (B7/B8).
// ---------------------------------------------------------------------------
export const CAMPAIGN_TRAFFIC_SOURCES = [
  'FACEBOOK',
  'INSTAGRAM',
  'GOOGLE',
  'TIKTOK',
  'FLYER_QR',
  'OTHER',
] as const;
export type CampaignTrafficSource = (typeof CAMPAIGN_TRAFFIC_SOURCES)[number];

export const CAMPAIGN_TRAFFIC_SOURCE_LABELS: Record<CampaignTrafficSource, string> = {
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  GOOGLE: 'Google',
  TIKTOK: 'TikTok',
  FLYER_QR: 'Flyer / QR code',
  OTHER: 'Other',
};
